<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const store = useStore();
const i18nPath = 'INBOX_MGMT.WIDGET_BUILDER.WIDGET_CUSTOMIZATION';

const globalConfig = computed(() => store.getters['globalConfig/get']);

// State
const activeTab = ref('theme');
const previewScreen = ref('home');

const colors = ref({
  primary: '#1F93FF',
  user_bubble: '#1F93FF',
  user_bubble_text: '#FFFFFF',
  agent_bubble: '#F3F4F6',
  agent_bubble_text: '#111827',
});

const bubble = ref({
  shape: 'circle',
  size: 'md',
  position: 'right',
  animation: 'none',
  hover_effect: 'scale',
});

const chatWindow = ref({
  border_radius: 16,
  header_style: 'solid',
  show_branding: true,
});

const selectedTemplate = ref('standard');

// Configuration options
const tabs = computed(() => [
  { id: 'theme', name: t(`${i18nPath}.TABS.THEME`), icon: 'i-lucide-palette' },
  {
    id: 'bubble',
    name: t(`${i18nPath}.TABS.BUTTON`),
    icon: 'i-lucide-message-circle',
  },
  { id: 'window', name: t(`${i18nPath}.TABS.WINDOW`), icon: 'i-lucide-layout' },
  {
    id: 'templates',
    name: t(`${i18nPath}.TABS.TEMPLATES`),
    icon: 'i-lucide-sparkles',
  },
]);

const quickColors = [
  '#1F93FF',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#EF4444',
  '#F59E0B',
  '#10B981',
  '#06B6D4',
];

const bubbleShapes = [
  { id: 'circle', radius: '50%' },
  { id: 'rounded', radius: '12px' },
  { id: 'square', radius: '4px' },
];

const bubbleSizes = [
  { id: 'sm', label: 'P', px: 48 },
  { id: 'md', label: 'M', px: 56 },
  { id: 'lg', label: 'G', px: 64 },
];

const bubbleAnimations = computed(() => [
  { id: 'none', name: t(`${i18nPath}.ANIMATIONS.NONE`) },
  { id: 'pulse', name: t(`${i18nPath}.ANIMATIONS.PULSE`) },
]);

const headerStyles = computed(() => [
  { id: 'solid', name: t(`${i18nPath}.HEADER_STYLES.SOLID`) },
  { id: 'gradient', name: t(`${i18nPath}.HEADER_STYLES.GRADIENT`) },
]);

const templates = computed(() => [
  {
    id: 'standard',
    name: t(`${i18nPath}.TEMPLATES.STANDARD`),
    colors: {
      primary: '#1F93FF',
      user_bubble: '#1F93FF',
      agent_bubble: '#F3F4F6',
      agent_bubble_text: '#111827',
    },
    bubble: { shape: 'circle', size: 'md' },
    chatWindow: { border_radius: 16, header_style: 'solid' },
  },
  {
    id: 'modern',
    name: t(`${i18nPath}.TEMPLATES.MODERN`),
    colors: {
      primary: '#6366F1',
      user_bubble: '#6366F1',
      agent_bubble: '#F1F5F9',
      agent_bubble_text: '#1E293B',
    },
    bubble: { shape: 'rounded', size: 'md' },
    chatWindow: { border_radius: 20, header_style: 'gradient' },
  },
  {
    id: 'corporate',
    name: t(`${i18nPath}.TEMPLATES.CORPORATE`),
    colors: {
      primary: '#1E40AF',
      user_bubble: '#1E40AF',
      agent_bubble: '#F8FAFC',
      agent_bubble_text: '#0F172A',
    },
    bubble: { shape: 'square', size: 'md' },
    chatWindow: { border_radius: 8, header_style: 'solid' },
  },
  {
    id: 'playful',
    name: t(`${i18nPath}.TEMPLATES.PLAYFUL`),
    colors: {
      primary: '#EC4899',
      user_bubble: '#EC4899',
      agent_bubble: '#FDF2F8',
      agent_bubble_text: '#831843',
    },
    bubble: { shape: 'circle', size: 'lg', animation: 'pulse' },
    chatWindow: { border_radius: 24, header_style: 'gradient' },
  },
  {
    id: 'dark',
    name: t(`${i18nPath}.TEMPLATES.DARK`),
    colors: {
      primary: '#8B5CF6',
      user_bubble: '#8B5CF6',
      agent_bubble: '#374151',
      agent_bubble_text: '#F9FAFB',
    },
    bubble: { shape: 'circle', size: 'md' },
    chatWindow: { border_radius: 16, header_style: 'solid' },
  },
  {
    id: 'minimal',
    name: t(`${i18nPath}.TEMPLATES.MINIMAL`),
    colors: {
      primary: '#374151',
      user_bubble: '#374151',
      agent_bubble: '#F9FAFB',
      agent_bubble_text: '#111827',
    },
    bubble: { shape: 'circle', size: 'sm' },
    chatWindow: { border_radius: 12, header_style: 'solid' },
  },
]);

// Initialize from inbox
watch(
  () => props.inbox,
  inbox => {
    if (!inbox?.id) return;
    if (inbox.widget_color) {
      colors.value.primary = inbox.widget_color;
      colors.value.user_bubble = inbox.widget_color;
    }
    if (inbox.widget_colors)
      colors.value = { ...colors.value, ...inbox.widget_colors };
    if (inbox.bubble_config)
      bubble.value = { ...bubble.value, ...inbox.bubble_config };
    if (inbox.chat_window_config)
      chatWindow.value = { ...chatWindow.value, ...inbox.chat_window_config };
    if (inbox.widget_template) selectedTemplate.value = inbox.widget_template;
  },
  { immediate: true, deep: true }
);

// Helpers
function darkenColor(hex, amount) {
  const clean = hex.replace('#', '');
  const r = Math.max(
    0,
    Math.min(255, parseInt(clean.substring(0, 2), 16) + amount)
  );
  const g = Math.max(
    0,
    Math.min(255, parseInt(clean.substring(2, 4), 16) + amount)
  );
  const b = Math.max(
    0,
    Math.min(255, parseInt(clean.substring(4, 6), 16) + amount)
  );
  const toHex = n => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Computed
const bubblePx = computed(
  () => ({ sm: 48, md: 56, lg: 64 })[bubble.value.size] || 56
);
const bubbleRadius = computed(
  () =>
    ({ circle: '50%', rounded: '12px', square: '4px' })[bubble.value.shape] ||
    '50%'
);

const headerBg = computed(() => {
  if (chatWindow.value.header_style === 'gradient') {
    return `linear-gradient(135deg, ${colors.value.primary}, ${darkenColor(colors.value.primary, -40)})`;
  }
  return colors.value.primary;
});

// Actions
function applyTemplate(tpl) {
  selectedTemplate.value = tpl.id;
  colors.value = { ...colors.value, ...tpl.colors };
  bubble.value = { ...bubble.value, ...tpl.bubble };
  chatWindow.value = { ...chatWindow.value, ...tpl.chatWindow };
}

function save() {
  emit('update', {
    widget_colors: colors.value,
    bubble_config: bubble.value,
    chat_window_config: chatWindow.value,
    widget_template: selectedTemplate.value,
  });
}

// Preview data
const widgetName = computed(() => props.inbox?.name || 'Suporte');
const welcomeTitle = computed(() => props.inbox?.welcome_title || 'Olá! 👋');
const welcomeTagline = computed(
  () => props.inbox?.welcome_tagline || 'Como podemos ajudar?'
);
</script>

<template>
  <div class="flex gap-8">
    <!-- Settings -->
    <div class="w-80 flex-shrink-0 space-y-6">
      <!-- Tabs -->
      <div class="flex p-1 bg-n-alpha-1 rounded-lg">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-all"
          :class="
            activeTab === tab.id
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = tab.id"
        >
          <span :class="tab.icon" class="size-3.5" />
          {{ tab.name }}
        </button>
      </div>

      <!-- Theme -->
      <div v-show="activeTab === 'theme'" class="space-y-5">
        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">{{
            t(`${i18nPath}.LABELS.PRIMARY_COLOR`)
          }}</label>
          <div class="flex gap-2 items-center">
            <input
              v-model="colors.primary"
              type="color"
              class="w-10 h-10 rounded-lg cursor-pointer border border-n-weak"
            />
            <input
              v-model="colors.primary"
              type="text"
              class="flex-1 px-3 py-2 text-sm font-mono border border-n-weak rounded-lg bg-n-background"
            />
          </div>
        </div>

        <div>
          <label class="text-xs text-n-slate-11 mb-2 block">{{
            t(`${i18nPath}.LABELS.QUICK_COLORS`)
          }}</label>
          <div class="flex gap-1.5">
            <button
              v-for="c in quickColors"
              :key="c"
              class="w-7 h-7 rounded-md transition-transform hover:scale-110"
              :class="
                colors.primary === c ? 'ring-2 ring-n-brand ring-offset-2' : ''
              "
              :style="{ background: c }"
              @click="
                colors.primary = c;
                colors.user_bubble = c;
              "
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="text-xs text-n-slate-11 mb-1.5 block">{{
              t(`${i18nPath}.LABELS.USER_BUBBLE`)
            }}</label>
            <div class="flex gap-1.5">
              <input
                v-model="colors.user_bubble"
                type="color"
                class="w-8 h-8 rounded cursor-pointer border border-n-weak"
              />
              <input
                v-model="colors.user_bubble"
                type="text"
                class="flex-1 px-2 py-1 text-xs font-mono border border-n-weak rounded bg-n-background"
              />
            </div>
          </div>
          <div>
            <label class="text-xs text-n-slate-11 mb-1.5 block">{{
              t(`${i18nPath}.LABELS.AGENT_BUBBLE`)
            }}</label>
            <div class="flex gap-1.5">
              <input
                v-model="colors.agent_bubble"
                type="color"
                class="w-8 h-8 rounded cursor-pointer border border-n-weak"
              />
              <input
                v-model="colors.agent_bubble"
                type="text"
                class="flex-1 px-2 py-1 text-xs font-mono border border-n-weak rounded bg-n-background"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Bubble -->
      <div v-show="activeTab === 'bubble'" class="space-y-5">
        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">{{
            t(`${i18nPath}.LABELS.SHAPE`)
          }}</label>
          <div class="flex gap-2">
            <button
              v-for="s in bubbleShapes"
              :key="s.id"
              class="flex-1 h-14 flex items-center justify-center border-2 rounded-lg transition-all"
              :class="
                bubble.shape === s.id
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak hover:border-n-slate-6'
              "
              @click="bubble.shape = s.id"
            >
              <div
                class="w-8 h-8"
                :style="{ background: colors.primary, borderRadius: s.radius }"
              />
            </button>
          </div>
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">{{
            t(`${i18nPath}.LABELS.SIZE`)
          }}</label>
          <div class="flex gap-2">
            <button
              v-for="s in bubbleSizes"
              :key="s.id"
              class="flex-1 py-2.5 text-center border-2 rounded-lg transition-all"
              :class="
                bubble.size === s.id
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak hover:border-n-slate-6'
              "
              @click="bubble.size = s.id"
            >
              <span class="text-sm font-semibold text-n-slate-12">{{
                s.label
              }}</span>
              <span class="text-xs text-n-slate-10 ml-1">{{ s.px }}px</span>
            </button>
          </div>
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">{{
            t(`${i18nPath}.LABELS.ANIMATION`)
          }}</label>
          <div class="flex gap-2">
            <button
              v-for="a in bubbleAnimations"
              :key="a.id"
              class="flex-1 py-2 text-sm border-2 rounded-lg transition-all"
              :class="
                bubble.animation === a.id
                  ? 'border-n-brand bg-n-brand/5 text-n-brand'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="bubble.animation = a.id"
            >
              {{ a.name }}
            </button>
          </div>
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">{{
            t(`${i18nPath}.LABELS.POSITION`)
          }}</label>
          <div class="flex gap-2">
            <button
              v-for="p in ['right', 'left']"
              :key="p"
              class="flex-1 py-2 text-sm border-2 rounded-lg transition-all"
              :class="
                bubble.position === p
                  ? 'border-n-brand bg-n-brand/5 text-n-brand'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="bubble.position = p"
            >
              {{
                p === 'right'
                  ? t(`${i18nPath}.POSITIONS.RIGHT`)
                  : t(`${i18nPath}.POSITIONS.LEFT`)
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Window -->
      <div v-show="activeTab === 'window'" class="space-y-5">
        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">{{
            t(`${i18nPath}.LABELS.HEADER`)
          }}</label>
          <div class="flex gap-2">
            <button
              v-for="s in headerStyles"
              :key="s.id"
              class="flex-1 flex flex-col items-center gap-2 p-3 border-2 rounded-lg transition-all"
              :class="
                chatWindow.header_style === s.id
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak hover:border-n-slate-6'
              "
              @click="chatWindow.header_style = s.id"
            >
              <div
                class="w-full h-5 rounded"
                :style="{
                  background:
                    s.id === 'gradient'
                      ? `linear-gradient(135deg, ${colors.primary}, ${darkenColor(colors.primary, -40)})`
                      : colors.primary,
                }"
              />
              <span class="text-xs text-n-slate-12">{{ s.name }}</span>
            </button>
          </div>
        </div>

        <div>
          <div class="flex justify-between mb-2">
            <label class="text-sm font-medium text-n-slate-12">{{
              t(`${i18nPath}.LABELS.BORDERS`)
            }}</label>
            <span class="text-xs font-mono text-n-slate-10"
              >{{ chatWindow.border_radius }}px</span
            >
          </div>
          <input
            v-model.number="chatWindow.border_radius"
            type="range"
            min="0"
            max="24"
            class="w-full h-1.5 bg-n-slate-3 rounded appearance-none cursor-pointer accent-n-brand"
          />
        </div>

        <label
          class="flex items-center justify-between p-3 rounded-lg border border-n-weak cursor-pointer hover:bg-n-alpha-1"
        >
          <span class="text-sm text-n-slate-12">{{
            t(`${i18nPath}.LABELS.SHOW_BRANDING`)
          }}</span>
          <input
            v-model="chatWindow.show_branding"
            type="checkbox"
            class="w-4 h-4 accent-n-brand"
          />
        </label>
      </div>

      <!-- Templates -->
      <div v-show="activeTab === 'templates'" class="space-y-2">
        <button
          v-for="tpl in templates"
          :key="tpl.id"
          class="w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all"
          :class="
            selectedTemplate === tpl.id
              ? 'border-n-brand bg-n-brand/5'
              : 'border-n-weak hover:border-n-slate-6'
          "
          @click="applyTemplate(tpl)"
        >
          <div
            class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            :style="{ background: tpl.colors.primary }"
          >
            <span class="i-lucide-message-circle size-4 text-white" />
          </div>
          <span class="text-sm font-medium text-n-slate-12">{{
            tpl.name
          }}</span>
          <span
            v-if="selectedTemplate === tpl.id"
            class="i-lucide-check size-4 text-n-brand ml-auto"
          />
        </button>
      </div>

      <!-- Save -->
      <NextButton
        :label="
          t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.BUTTON_TEXT')
        "
        :is-loading="isLoading"
        class="w-full"
        @click="save"
      />
    </div>

    <!-- Preview -->
    <div class="flex-1 flex flex-col min-w-0">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-medium text-n-slate-11">{{
          t(`${i18nPath}.LABELS.PREVIEW`)
        }}</span>
        <div class="flex p-0.5 bg-n-alpha-1 rounded-md">
          <button
            v-for="s in ['home', 'chat']"
            :key="s"
            class="px-2.5 py-1 text-xs rounded transition-all"
            :class="
              previewScreen === s
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11'
            "
            @click="previewScreen = s"
          >
            {{
              s === 'home'
                ? t(`${i18nPath}.PREVIEW.HOME`)
                : t(`${i18nPath}.PREVIEW.CHAT`)
            }}
          </button>
        </div>
      </div>

      <div
        class="flex-1 flex items-end p-6 bg-gradient-to-br from-n-slate-2 to-n-slate-3 dark:from-n-solid-2 dark:to-n-solid-3 rounded-xl overflow-hidden"
        :class="bubble.position === 'left' ? 'justify-start' : 'justify-end'"
      >
        <div class="w-80">
          <!-- Window -->
          <div
            class="bg-white dark:bg-n-solid-2 shadow-xl overflow-hidden mb-3"
            :style="{ borderRadius: `${chatWindow.border_radius}px` }"
          >
            <!-- Header -->
            <div class="p-4" :style="{ background: headerBg }">
              <div class="flex items-center gap-3">
                <div
                  class="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <span class="i-lucide-message-circle size-4 text-white" />
                </div>
                <div v-if="previewScreen === 'chat'">
                  <div class="text-sm font-medium text-white">
                    {{ widgetName }}
                  </div>
                  <div class="flex items-center gap-1 text-xs text-white/80">
                    <span class="w-1.5 h-1.5 rounded-full bg-green-400" />
                    {{ t(`${i18nPath}.PREVIEW.ONLINE`) }}
                  </div>
                </div>
              </div>
              <div v-if="previewScreen === 'home'" class="mt-3">
                <div class="text-lg font-semibold text-white">
                  {{ welcomeTitle }}
                </div>
                <div class="text-sm text-white/80">{{ welcomeTagline }}</div>
              </div>
            </div>

            <!-- Body -->
            <div class="bg-n-slate-1 dark:bg-n-solid-1">
              <!-- Home -->
              <div v-if="previewScreen === 'home'" class="p-4">
                <div
                  class="p-3 bg-white dark:bg-n-solid-2 rounded-lg border border-n-weak"
                >
                  <div class="flex items-center gap-2 mb-3">
                    <div
                      class="w-8 h-8 rounded-full bg-n-slate-2 dark:bg-n-solid-3 flex items-center justify-center"
                    >
                      <span class="i-lucide-user size-4 text-n-slate-11" />
                    </div>
                    <div>
                      <div class="text-sm font-medium text-n-slate-12">
                        {{ t(`${i18nPath}.PREVIEW.TEAM`) }}
                      </div>
                      <div class="text-xs text-n-slate-10">
                        {{ t(`${i18nPath}.PREVIEW.ONLINE_NOW`) }}
                      </div>
                    </div>
                  </div>
                  <button
                    class="w-full py-2 text-sm font-medium text-white rounded-md"
                    :style="{ background: colors.primary }"
                  >
                    {{ t(`${i18nPath}.PREVIEW.START_CONVERSATION`) }}
                  </button>
                </div>
              </div>

              <!-- Chat -->
              <div v-else class="p-4 space-y-2">
                <div class="flex justify-end">
                  <div
                    class="px-3 py-2 rounded-2xl rounded-br-sm text-sm max-w-[80%]"
                    :style="{
                      background: colors.user_bubble,
                      color: colors.user_bubble_text,
                    }"
                  >
                    {{ t(`${i18nPath}.PREVIEW.HELLO`) }}
                  </div>
                </div>
                <div class="flex gap-2">
                  <div
                    class="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center"
                    :style="{ background: colors.agent_bubble }"
                  >
                    <span
                      class="i-lucide-bot size-3"
                      :style="{ color: colors.agent_bubble_text }"
                    />
                  </div>
                  <div
                    class="px-3 py-2 rounded-2xl rounded-bl-sm text-sm"
                    :style="{
                      background: colors.agent_bubble,
                      color: colors.agent_bubble_text,
                    }"
                  >
                    {{ t(`${i18nPath}.PREVIEW.HOW_CAN_HELP`) }}
                  </div>
                </div>
              </div>

              <!-- Input -->
              <div
                v-if="previewScreen === 'chat'"
                class="p-3 border-t border-n-weak bg-white dark:bg-n-solid-2"
              >
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-n-slate-1 dark:bg-n-solid-1 rounded-full text-sm text-n-slate-10"
                >
                  <span class="i-lucide-paperclip size-4" />
                  <span class="flex-1">{{
                    t(`${i18nPath}.PREVIEW.MESSAGE_PLACEHOLDER`)
                  }}</span>
                  <span
                    class="i-lucide-send size-4"
                    :style="{ color: colors.primary }"
                  />
                </div>
              </div>
            </div>

            <!-- Branding -->
            <div
              v-if="chatWindow.show_branding"
              class="py-1.5 text-center text-xs text-n-slate-10 bg-n-slate-1 dark:bg-n-solid-1 border-t border-n-weak flex items-center justify-center gap-1"
            >
              <img
                v-if="globalConfig?.logoThumbnail"
                :src="globalConfig.logoThumbnail"
                class="w-3 h-3"
                alt=""
              />
              {{ t('INBOX_MGMT.WIDGET_BUILDER.BRANDING_TEXT') }}
            </div>
          </div>

          <!-- Bubble -->
          <div
            :class="
              bubble.position === 'left'
                ? 'flex justify-start'
                : 'flex justify-end'
            "
          >
            <div
              class="flex items-center justify-center shadow-lg cursor-pointer"
              :class="bubble.animation === 'pulse' ? 'animate-pulse' : ''"
              :style="{
                width: `${bubblePx}px`,
                height: `${bubblePx}px`,
                borderRadius: bubbleRadius,
                background: colors.primary,
              }"
            >
              <svg viewBox="0 0 240 240" fill="none" class="w-6 h-6">
                <path
                  d="M240.808 240.808H122.123C56.6994 240.808 3.45695 187.562 3.45695 122.122C3.45695 56.7031 56.6994 3.45697 122.124 3.45697C187.566 3.45697 240.808 56.7031 240.808 122.122V240.808Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
